import React, {
    useState,
    useMemo,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Button } from '@appkit4/react-components/button';
import { Search } from '@appkit4/react-components/search';
import {
    type ColumnDef,
    type ColumnFiltersState,
    type RowSelectionState,
    type SortingState,
} from '@tanstack/react-table';
import MenuButton, { MenuItem } from '../../components/reusables/MenuButton';
import Pagination from '../../components/reusables/Pagination';
import DataTable from '../../components/reusables/DataTable';
import ResolvePerAssetModal from './ResolvePerAssetModal';
import ResolvePerRuleModal from './ResolvePerRuleModal';
import KeywordAnalysisModal, { Keyword } from './KeywordAnalysisModal';
import AddRuleModal, { SplitRuleValues } from './AddRuleModal';
import ManualCatModal from './ManualCatModal';
import ImportDataModal, {
    ImportDataDraft,
} from '../../components/reusables/ImportDataModal';
import EngagementSetup from './EngagementSetup';
import { useEngagement } from '../../context/EngagementContext';
import { useTenant } from '../../context/TenantContext';
import {
    getGridColumnVisibilityKey,
    readHiddenColumnIds,
    saveHiddenColumnIds,
} from '../../helpers/gridColumnVisibility';
import { DataSourceTypeId } from '../../Constants';
import {
    useGetDataGrid,
    useGetDatasetColumns,
    useGetGeneratedFixedAssetRules,
    useGetUserInfo,
    useApplyManualFixedAssetRule,
    useApplyManualFixedAssetPoolSplits,
    getGetFAProgressBarStatusQueryKey,
    getGetDatasetColumnsQueryKey,
    useUploadImportFile,
    useCommitFixedAssetImport,
    useGetDataFieldDefinitions,
} from '../../api/generated/endpoint';
import { useCustomAxios } from '../../api/mutator/custom-instance';
import {
    getImportUploadErrorMessage,
    ImportUploadResponse,
} from '../../api/imports';
import type { PoolResponse } from '../../api/generated/models';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import useToast from '../../helpers/useToast';

const PAGE_SIZE = 25;
const DESCRIPTION_COLUMN_MAX_WIDTH = 360;

const runRulesItems: MenuItem[] = [
    { label: 'Deterministic Rules', value: 'deterministic' },
    { label: 'AI Rules', value: 'ai' },
];

const conflictResolveItems: MenuItem[] = [
    { label: 'Resolve Per Asset', value: 'per-asset' },
    { label: 'Resolve Per Rule', value: 'per-rule' },
    { label: 'Prior Year Treatment', value: 'prior-year' },
];

const dataItems: MenuItem[] = [
    { label: 'Import from File', value: 'import' },
    { label: 'Download Template', value: 'download-template' },
    { label: 'Delete', value: 'delete' },
];

const FixedAssetData: React.FC = () => {
    const queryClient = useQueryClient();
    const { selectedEntityId, activeTimeId } = useEngagement();
    const { selectedTenant } = useTenant();
    const { data: userInfo } = useGetUserInfo();
    const { id: routeTenantId } = useParams();
    const parsedRouteTenantId = Number(routeTenantId);
    const tenantId =
        Number.isInteger(parsedRouteTenantId) && parsedRouteTenantId > 0
            ? parsedRouteTenantId
            : (selectedTenant?.tenantId ?? userInfo?.tenants?.[0]?.tenantId);

    const [searchValue, setSearchValue] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

    // Fetch columns for toggle menu
    const { data: columnsData } = useGetDatasetColumns(
        {
            entityId: selectedEntityId!,
            timeId: activeTimeId!,
            dataSourceTypeId: DataSourceTypeId.FixedAsset,
        },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );

    const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<number>>(
        new Set()
    );

    const columnVisibilityKey = useMemo(
        () =>
            getGridColumnVisibilityKey({
                userId: userInfo?.id,
                tenantId,
                entityId: selectedEntityId,
                timeId: activeTimeId,
                dataSourceTypeId: DataSourceTypeId.FixedAsset,
            }),
        [activeTimeId, selectedEntityId, tenantId, userInfo?.id]
    );

    useEffect(() => {
        setHiddenColumnIds(readHiddenColumnIds(columnVisibilityKey));
    }, [columnVisibilityKey]);

    const [sortColumnId, setSortColumnId] = useState<number | undefined>(
        undefined
    );
    const [sortDescending, setSortDescending] = useState<boolean>(false);
    const [columnFilters, setColumnFilters] = useState<Record<number, string>>(
        {}
    );

    // Fetch paginated grid data
    const gridDataQuery = useGetDataGrid(
        {
            EntityId: selectedEntityId!,
            TimeId: activeTimeId!,
            DataSourceTypeId: DataSourceTypeId.FixedAsset,
            Skip: (currentPage - 1) * pageSize,
            Take: pageSize,
            SearchTerm: searchValue || undefined,
            SortColumnId: sortColumnId,
            SortDescending: sortDescending,
            ColumnFilters:
                Object.keys(columnFilters).length > 0
                    ? JSON.stringify(columnFilters)
                    : undefined,
            IncludeAssignments: true,
            HiddenColumnIds: [...hiddenColumnIds].sort(
                (left, right) => left - right
            ),
        },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );
    const { data: gridData, isLoading } = gridDataQuery;

    const [resolvePerAssetModalVisible, setResolvePerAssetModalVisible] =
        useState<boolean>(false);
    const [resolvePerRuleModalVisible, setResolvePerRuleModalVisible] =
        useState<boolean>(false);
    const [keywordAnalysisModalVisible, setKeywordAnalysisModalVisible] =
        useState<boolean>(false);
    const [createSplitRuleModalVisible, setCreateSplitRuleModalVisible] =
        useState<boolean>(false);
    const [uploadModalVisible, setUploadModalVisible] =
        useState<boolean>(false);
    const [importUpload, setImportUpload] = useState<ImportUploadResponse>();
    const [isImportPreviewLoading, setIsImportPreviewLoading] =
        useState<boolean>(false);
    const [importPreviewError, setImportPreviewError] = useState<string>();
    const importUploadRequestId = useRef(0);
    const [manualCategoriseModalVisible, setManualCategoriseModalVisible] =
        useState<boolean>(false);
    const [rowSelectionState, setRowSelectionState] =
        useState<RowSelectionState>({});
    const selectedRowIds = useMemo(
        () =>
            Object.keys(rowSelectionState)
                .filter((rowId) => rowSelectionState[rowId])
                .map(Number),
        [rowSelectionState]
    );
    const getPools = useCustomAxios<PoolResponse[]>();
    const uploadImport = useUploadImportFile();
    const commitImport = useCommitFixedAssetImport();
    const [isImportingData, setIsImportingData] = useState<boolean>(false);
    const { data: fieldDefinitions } = useGetDataFieldDefinitions({
        dataSourceTypeId: DataSourceTypeId.FixedAsset,
    });
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const { data: generatedRules, isLoading: isLoadingGeneratedRules } =
        useGetGeneratedFixedAssetRules(
            { entityId: selectedEntityId!, timeId: activeTimeId! },
            {
                query: {
                    enabled:
                        manualCategoriseModalVisible &&
                        !!selectedEntityId &&
                        !!activeTimeId,
                },
            }
        );
    const applyManualFixedAssetRule = useApplyManualFixedAssetRule();
    const applyManualFixedAssetPoolSplits =
        useApplyManualFixedAssetPoolSplits();
    const { data: pools, isLoading: isLoadingPools } = useQuery(
        ['manual-assignment-pools', selectedEntityId, activeTimeId],
        () =>
            getPools({
                url: '/v1/Pools',
                method: 'GET',
                params: { entityId: selectedEntityId, timeId: activeTimeId },
            }),
        {
            enabled:
                manualCategoriseModalVisible &&
                !!selectedEntityId &&
                !!activeTimeId,
        }
    );

    const [selectedKeyword, setSelectedKeyword] = useState<Keyword | null>(
        null
    );

    const handleSearch = (value: string) => {
        setSearchValue(value);
        setCurrentPage(1);
    };

    const handleConflictResolve = (value: string) => {
        if (value === 'per-asset') {
            setResolvePerAssetModalVisible(true);
        }
        if (value === 'per-rule') {
            setResolvePerRuleModalVisible(true);
        }
    };

    const handleRunRules = (value: string, item: MenuItem) => {
        console.log('Run Rules:', value, item);
    };

    const columnIdByKey = useMemo(() => {
        const map = new Map<string, number>();
        columnsData?.forEach((c) => {
            if (c.columnKey && c.columnId != null) {
                map.set(c.columnKey, c.columnId);
            }
        });
        return map;
    }, [columnsData]);

    const handleSortingChange = useCallback(
        (state: SortingState) => {
            const next = state[0];
            setSortColumnId(next ? columnIdByKey.get(next.id) : undefined);
            setSortDescending(next?.desc ?? false);
            setCurrentPage(1);
        },
        [columnIdByKey]
    );

    const handleColumnFiltersChange = useCallback(
        (state: ColumnFiltersState) => {
            const next: Record<number, string> = {};
            state.forEach((f) => {
                const id = columnIdByKey.get(f.id);
                if (id != null && f.value != null && String(f.value) !== '') {
                    next[id] = String(f.value);
                }
            });
            setColumnFilters(next);
            setCurrentPage(1);
        },
        [columnIdByKey]
    );

    const handleToggleColumns = useCallback(
        (value: string) => {
            const columnId = Number(value);
            const next = new Set(hiddenColumnIds);

            if (next.has(columnId)) {
                next.delete(columnId);
            } else {
                next.add(columnId);
            }

            setHiddenColumnIds(next);
            saveHiddenColumnIds(columnVisibilityKey, next);
        },
        [columnVisibilityKey, hiddenColumnIds]
    );

    const handleDownloadReport = () => {
        console.log('Download Report clicked');
    };

    const handleDataAction = (value: string, item: MenuItem) => {
        if (value === 'import') {
            setImportUpload(undefined);
            setImportPreviewError(undefined);
            setUploadModalVisible(true);
        }
        console.log('Data action:', value, item);
    };

    const handleImportFileSelected = (file: File): void => {
        const requestId = ++importUploadRequestId.current;

        if (!selectedEntityId || !activeTimeId) {
            setImportPreviewError(
                'Select an entity and time period before importing a file.'
            );
            return;
        }

        setImportUpload(undefined);
        setImportPreviewError(undefined);
        setIsImportPreviewLoading(true);
        void uploadImport
            .mutateAsync({
                data: {
                    EntityId: selectedEntityId,
                    TimeId: activeTimeId,
                    File: file,
                },
            })
            .then((response) => {
                if (requestId === importUploadRequestId.current) {
                    setImportUpload(response);
                }
            })
            .catch((error: unknown) => {
                if (requestId === importUploadRequestId.current) {
                    setImportPreviewError(getImportUploadErrorMessage(error));
                }
            })
            .finally(() => {
                if (requestId === importUploadRequestId.current) {
                    setIsImportPreviewLoading(false);
                }
            });
    };

    const handleCleansedFileSelected = (
        response: ImportUploadResponse
    ): void => {
        // The cleansed file already went through the import pipeline, so only the preview is new.
        ++importUploadRequestId.current;
        setImportPreviewError(undefined);
        setIsImportPreviewLoading(false);
        setImportUpload(response);
    };

    const handleImportDraft = (draft: ImportDataDraft): void => {
        if (!selectedEntityId || !activeTimeId) {
            return;
        }

        const headers =
            importUpload?.worksheets?.[draft.worksheetIndex]?.headers ?? [];

        const fieldIdsByName = new Map(
            (fieldDefinitions ?? []).map((definition) => [
                definition.fieldName,
                definition.fieldId,
            ])
        );

        // The ordinal is the column's position in the file, which is what the import reads values by.
        const columns = [
            ...draft.baseMappings.map((mapping) => ({
                ordinal: headers.indexOf(mapping.sourceHeader),
                displayName: mapping.sourceHeader,
                dataType: 1,
                fieldId: fieldIdsByName.get(mapping.fieldName) ?? null,
                isVisible: true,
            })),
            ...draft.additionalHeaders.map((header) => ({
                ordinal: headers.indexOf(header),
                displayName: header,
                dataType: 1,
                fieldId: null,
                isVisible: true,
            })),
        ].filter((column) => column.ordinal >= 0);

        if (columns.length === 0) {
            setImportPreviewError(
                'The selected worksheet headers could not be matched to the mapping.'
            );
            return;
        }

        setImportPreviewError(undefined);
        setIsImportingData(true);

        void commitImport
            .mutateAsync({
                data: {
                    uploadId: draft.uploadId,
                    entityId: selectedEntityId,
                    timeId: activeTimeId,
                    worksheetIndex: draft.worksheetIndex,
                    mode: draft.mode,
                    columns,
                },
            })
            .then((response) => {
                setUploadModalVisible(false);
                setImportUpload(undefined);
                showToast(
                    'Success',
                    `${response.rowCount ?? 0} rows were imported.`,
                    'success'
                );
                void queryClient.invalidateQueries({
                    queryKey: getGetDatasetColumnsQueryKey({
                        entityId: selectedEntityId,
                        timeId: activeTimeId,
                        dataSourceTypeId: DataSourceTypeId.FixedAsset,
                    }),
                });
                void queryClient.invalidateQueries({
                    queryKey: getGetFAProgressBarStatusQueryKey({
                        entityId: selectedEntityId,
                        timeId: activeTimeId,
                    }),
                });
                void gridDataQuery.refetch();
            })
            .catch((error: unknown) => {
                setImportPreviewError(getImportUploadErrorMessage(error));
            })
            .finally(() => {
                setIsImportingData(false);
            });
    };

    const handleResetRules = () => {
        console.log('Reset rules');
    };

    const handleKeywordAnalysis = () => {
        setKeywordAnalysisModalVisible(true);
    };

    const handleAssignmentSuccess = () => {
        setManualCategoriseModalVisible(false);
        setRowSelectionState({});
        showToast(
            'Success',
            'Manual categorisations have been applied.',
            'success'
        );
        void gridDataQuery.refetch();
        void queryClient.invalidateQueries({
            queryKey: getGetFAProgressBarStatusQueryKey({
                entityId: selectedEntityId,
                timeId: activeTimeId,
            }),
        });
    };

    const handleApplyManualRule = (ruleId: number) => {
        if (!selectedEntityId || !activeTimeId || selectedRowIds.length === 0) {
            return;
        }

        applyManualFixedAssetRule.mutate(
            {
                data: {
                    entityId: selectedEntityId,
                    timeId: activeTimeId,
                    ruleId,
                    rowIds: selectedRowIds,
                },
            },
            {
                onSuccess: handleAssignmentSuccess,
                onError: showGenericErrorToast,
            }
        );
    };

    const handleApplyManualPoolSplits = (
        poolSplits: { poolId: number; splitPercent: number }[]
    ) => {
        if (!selectedEntityId || !activeTimeId || selectedRowIds.length === 0) {
            return;
        }

        applyManualFixedAssetPoolSplits.mutate(
            {
                data: {
                    entityId: selectedEntityId,
                    timeId: activeTimeId,
                    rowIds: selectedRowIds,
                    poolSplits,
                },
            },
            {
                onSuccess: handleAssignmentSuccess,
                onError: showGenericErrorToast,
            }
        );
    };

    const handleCreateRuleFromKeyword = (keyword: Keyword) => {
        setSelectedKeyword(keyword);
        setKeywordAnalysisModalVisible(false);
        setCreateSplitRuleModalVisible(true);
    };

    const handleSaveSplitRule = (values: SplitRuleValues) => {
        console.log('Split rule created:', values);
        setSelectedKeyword(null);
    };

    const handleCloseCreateSplitRule = () => {
        setCreateSplitRuleModalVisible(false);
        setSelectedKeyword(null);
    };

    // Build toggle menu items from every column defined for the dataset.
    const toggleColumnsItems = useMemo<MenuItem[]>(() => {
        if (!columnsData) return [];
        return columnsData.map((c) => ({
            label: c.displayName ?? c.columnKey ?? '',
            value: String(c.columnId),
        }));
    }, [columnsData]);

    // Build flat row data for DataTable from API response
    const tableData = useMemo(() => {
        if (!gridData?.rows) return [];
        return gridData.rows.map((row) => ({
            ...row.values,
            _rowId: row.rowId,
            _categorisation: row.categorisation,
        }));
    }, [gridData]);

    // Build TanStack columns dynamically from API column definitions
    const faColumns = useMemo<ColumnDef<any, any>[]>(() => {
        if (!columnsData) return [];

        const dataCols: (ColumnDef<any, any> & { id: string })[] = columnsData
            .filter((c) => !hiddenColumnIds.has(c.columnId!))
            .map((c) => {
                const key = c.columnKey ?? '';
                const isNumeric = c.dataType === 2;
                const col: ColumnDef<any, any> & { id: string } = {
                    accessorKey: key,
                    id: key,
                    header: c.displayName ?? key,
                    enableSorting: true,
                };
                if (key === 'AssetDescription') {
                    col.size = DESCRIPTION_COLUMN_MAX_WIDTH;
                    col.maxSize = DESCRIPTION_COLUMN_MAX_WIDTH;
                }
                if (isNumeric) {
                    col.cell = ({ getValue }) => {
                        const val = getValue() as number | null;
                        if (val == null) return '';
                        return (
                            <span
                                className={
                                    val < 0
                                        ? 'cost-value cost-value--negative'
                                        : 'cost-value'
                                }
                            >
                                {val.toLocaleString('en-GB', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </span>
                        );
                    };
                }
                return col;
            });

        return dataCols;
    }, [columnsData, hiddenColumnIds]);

    const totalPages = Math.ceil((gridData?.filteredRows ?? 0) / pageSize);

    return (
        <div className="fixed-asset-data">
            <div className="fixed-asset-data__header">
                <h1
                    className="fixed-asset-data__title"
                    data-testid="fixed-asset-page-title"
                >
                    Fixed Asset Data
                </h1>
                <div className="fixed-asset-data__header-actions">
                    <Button
                        kind="secondary"
                        icon="icon-refresh-outline"
                        onClick={handleResetRules}
                    >
                        Reset Rules
                    </Button>
                    <MenuButton
                        label="Run Rules"
                        items={runRulesItems}
                        onSelect={handleRunRules}
                    />
                </div>
            </div>

            <div className="fixed-asset-data__toolbar">
                <div className="fixed-asset-data__search">
                    <Search
                        placeholder="Search"
                        value={searchValue}
                        onChange={(value) => handleSearch(value)}
                        onClear={() => setSearchValue('')}
                        noList
                    />
                </div>

                <div className="fixed-asset-data__toolbar-actions">
                    <Button kind="secondary" onClick={handleKeywordAnalysis}>
                        Keyword Analysis
                    </Button>

                    <MenuButton
                        label="Conflict Resolve"
                        items={conflictResolveItems}
                        kind="secondary"
                        onSelect={handleConflictResolve}
                    />

                    <MenuButton
                        label="Toggle Columns"
                        kind="secondary"
                        testId="fixed-asset-toggle-columns"
                        keepOpenOnSelect
                        items={toggleColumnsItems.map((column) => ({
                            ...column,
                            testId: `fixed-asset-column-${column.value}`,
                            label: `${
                                !hiddenColumnIds.has(Number(column.value))
                                    ? '✓ '
                                    : ''
                            }${column.label}`,
                        }))}
                        onSelect={handleToggleColumns}
                    />

                    <MenuButton
                        label="Data"
                        items={dataItems}
                        kind="secondary"
                        onSelect={handleDataAction}
                    />
                    <Button kind="secondary" onClick={handleDownloadReport}>
                        Download Report
                    </Button>
                </div>
            </div>

            {selectedRowIds.length > 0 && (
                <div className="fixed-asset-data__selection-bar">
                    <span>
                        {selectedRowIds.length}{' '}
                        {selectedRowIds.length === 1 ? 'Asset' : 'Assets'}{' '}
                        Selected
                    </span>
                    <div>
                        <Button
                            kind="secondary"
                            onClick={() => {
                                setRowSelectionState({});
                            }}
                        >
                            Clear Selection
                        </Button>
                        <Button
                            kind="secondary"
                            onClick={() =>
                                setManualCategoriseModalVisible(true)
                            }
                        >
                            Manually categorize
                        </Button>
                    </div>
                </div>
            )}

            <div className="fixed-asset-data__table">
                {isLoading ? (
                    <p>Loading...</p>
                ) : (
                    <DataTable
                        data={tableData}
                        columns={faColumns}
                        sorting
                        manualSorting
                        onSortingChange={handleSortingChange}
                        columnFiltering
                        manualFiltering
                        onColumnFiltersChange={handleColumnFiltersChange}
                        manualPagination
                        pageSize={pageSize}
                        totalRows={gridData?.filteredRows ?? 0}
                        rowSelection
                        getRowId={(row) => String(row._rowId)}
                        rowSelectionState={rowSelectionState}
                        onRowSelectionStateChange={setRowSelectionState}
                    />
                )}
            </div>

            <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={gridData?.filteredRows ?? 0}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
            />

            {resolvePerAssetModalVisible && (
                <ResolvePerAssetModal
                    visible={resolvePerAssetModalVisible}
                    onClose={() => setResolvePerAssetModalVisible(false)}
                    context={{ source: 'fixedAsset' }}
                    onResolve={(context, assetId, chosenRule) => {
                        console.log('Resolved:', context, assetId, chosenRule);
                    }}
                />
            )}

            {resolvePerRuleModalVisible && (
                <ResolvePerRuleModal
                    visible={resolvePerRuleModalVisible}
                    onClose={() => setResolvePerRuleModalVisible(false)}
                    context={{ source: 'fixedAsset' }}
                    onResolve={(
                        context,
                        chosenRule,
                        rejectedRule,
                        numberOfAssets
                    ) => {
                        console.log(
                            'Resolved per rule:',
                            context,
                            chosenRule,
                            'rejected:',
                            rejectedRule,
                            'assets:',
                            numberOfAssets
                        );
                    }}
                />
            )}

            {keywordAnalysisModalVisible && (
                <KeywordAnalysisModal
                    visible={keywordAnalysisModalVisible}
                    onClose={() => setKeywordAnalysisModalVisible(false)}
                    onCreateRule={handleCreateRuleFromKeyword}
                    onResolve={() => {
                        setKeywordAnalysisModalVisible(false);
                        setResolvePerAssetModalVisible(true);
                    }}
                />
            )}

            {createSplitRuleModalVisible && (
                <AddRuleModal
                    visible={createSplitRuleModalVisible}
                    onClose={handleCloseCreateSplitRule}
                    onSave={handleSaveSplitRule}
                    selectedKeyword={selectedKeyword}
                />
            )}

            {manualCategoriseModalVisible && (
                <ManualCatModal
                    visible={manualCategoriseModalVisible}
                    onClose={() => setManualCategoriseModalVisible(false)}
                    selectedRowCount={selectedRowIds.length}
                    onSavePoolSplits={handleApplyManualPoolSplits}
                    onSaveRule={handleApplyManualRule}
                    pools={pools ?? []}
                    generatedRules={generatedRules ?? []}
                    isLoadingPools={isLoadingPools}
                    isLoadingRules={isLoadingGeneratedRules}
                    isSaving={
                        applyManualFixedAssetRule.isLoading ||
                        applyManualFixedAssetPoolSplits.isLoading
                    }
                />
            )}

            {uploadModalVisible && (
                <ImportDataModal
                    visible={uploadModalVisible}
                    onClose={() => setUploadModalVisible(false)}
                    dataSourceTypeId={DataSourceTypeId.FixedAsset}
                    uploadId={importUpload?.uploadId}
                    worksheetPreviews={importUpload?.worksheets ?? undefined}
                    isPreviewLoading={isImportPreviewLoading}
                    isSubmitting={isImportingData}
                    hasExistingData={(gridData?.totalRows ?? 0) > 0}
                    previewError={importPreviewError}
                    entityId={selectedEntityId}
                    timeId={activeTimeId}
                    previousColumns={columnsData ?? undefined}
                    onFileSelected={handleImportFileSelected}
                    onCleansedFileSelected={handleCleansedFileSelected}
                    onSubmit={handleImportDraft}
                />
            )}
        </div>
    );
};

const FixedAssetPage = () => (
    <EngagementSetup>
        <FixedAssetData />
    </EngagementSetup>
);

export default FixedAssetPage;
